import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import {
	FREE_EDITION,
	STANDARD_EDITION,
	PREMIUM_EDITION,
} from '@jira/platform__application-editions/src/edition.tsx';
import { UNSAFE_noExposureExp, expValEquals } from '@atlassian/jira-feature-experiments';
import { isFedRamp } from '@atlassian/atl-context';
import { fg } from '@atlassian/jira-feature-gating';
import { BOARD_CREATE_COLUMN_NUDGE_ID } from '@atlassian/jira-onboarding-core/src/constants.tsx';
import {
	isTailoredOnboardingM2P5TailoredTeamType,
	isTailoredOnboardingTeamType,
} from '@atlassian/jira-tailored-onboarding/src/utils.tsx';
import {
	REVERSE_TRIAL_ROOT_TASK,
	ROOT_TASK,
	BACKLOG_CREATE_SPRINT_NUDGE_ID,
	PACKAGE_TEAM_NAME,
	PACKAGE_NAME,
} from '../../common/constants.tsx';
import type { Cards, TaskListItem, TaskList } from '../../common/types.tsx';
import { isRoadmapRoute } from '../../common/utils/roadmap-route.tsx';
import { USER_ROLE } from '../model/constants.tsx';
import type { ProjectState } from '../model/types.tsx';
import { JSM } from './config/jsm/index.tsx';
import { JSW } from './config/jsw/index.tsx';
import { JWM } from './config/jwm/index.tsx';
import { PLAN } from './config/plan/index.tsx';
import { getTasks } from './manager/index.tsx';
import type { GetTasksProps } from './types.tsx';
import { isJwmProject, isJsmProject } from './utils.tsx';
import {
	TAILORED_ONBOARDING_M2P5,
	TAILORED_ONBOARDING_BUSINESS,
	TAILORED_ONBOARDING_WITH_SINGLE_CONFIG,
} from './config/tailored-onboarding/index.tsx';
import { Experiment, FeatureGate } from './config/jsw/constants.tsx';
import { type TeamType, Feature } from './config/tailored-onboarding/constants.tsx';
import { TAILORED_ONBOARDING_BUSINESS_CONFIG } from './config/jwm/tailored-onboarding.tsx';

const getTaskListItem = ({
	variants,
	taskVariants,
	taskList,
	taskGroupParent,
}: {
	variants: string[];
	taskVariants: Cards;
	taskList: TaskList;
	taskGroupParent: string;
}): TaskListItem => {
	const [expConfig] = UNSAFE_noExposureExp('jira_rt_quickstart_m1');
	if (variants.includes(JSW.experiment.jswReverseTrial) && !expConfig.get('isEnabled', false)) {
		const { title, items } =
			taskList[taskGroupParent === ROOT_TASK ? REVERSE_TRIAL_ROOT_TASK : taskGroupParent] ??
			taskList[REVERSE_TRIAL_ROOT_TASK];
		return {
			title,
			items: getTasks(variants, taskVariants, items),
		};
	}
	const { title, items } = taskList[taskGroupParent] ?? taskList[ROOT_TASK];

	return {
		title,
		items: getTasks(variants, taskVariants, items),
	};
};

const createJswTasks = (
	projectState: ProjectState | null,
	taskGroupParent: string,
	routeName: string,
	shouldShowScrumOnboardingExperience: boolean,
	mountedNudges: string[],
	canCreateProject: boolean,
	isCrossProjectBoardToPlanUpsell: boolean,
	canEditPlans: boolean,
	premiumTrialOnSignup: boolean | undefined,
): TaskListItem => {
	const variants: Set<string> = new Set();
	const isTmpScrumWithSprintPermission = mountedNudges.includes(BACKLOG_CREATE_SPRINT_NUDGE_ID);

	const isEligibleForKanbanTour = mountedNudges.includes(BOARD_CREATE_COLUMN_NUDGE_ID);

	if (isFedRamp()) {
		variants.add(JSW.experience.fedRamp);
	}

	if (premiumTrialOnSignup) {
		variants.add(JSW.experiment.jswReverseTrial);
		expValEquals('jira_rt_quickstart_m1', 'isEnabled', true) &&
			variants.add(JSW.experiment.rtQuickstartM1);
	}

	if (shouldShowScrumOnboardingExperience && isTmpScrumWithSprintPermission) {
		variants.add(JSW.experiment.scrumOnboarding);
	}

	if (isCrossProjectBoardToPlanUpsell) {
		variants.add(JSW.trait.crossProjectBoardToPlanUpsell);
	}

	if (canEditPlans) {
		variants.add(JSW.permissions.hasPlanEditPermissions);
	}

	if (isEligibleForKanbanTour || routeName !== 'software-boards') {
		variants.add(JSW.experience.kanbanOnboarding);
	}

	if (canCreateProject === false) {
		variants.add(JSW.permissions.notCanCreateProject);
	}

	if (projectState?.isNextGen) {
		variants.add(JSW.projectStyle.teamManaged);
	} else {
		variants.add(JSW.projectStyle.companyManaged);
	}

	if (projectState?.isProjectAdmin) {
		variants.add(JSW.userRole.projectAdmin);
	}

	if (projectState?.userRole === USER_ROLE.JIRA_ADMIN) {
		variants.add(JSW.userRole.jiraAdmin);
		variants.add(JSW.userRole.admin);
	}

	if (projectState?.userRole === USER_ROLE.SITE_ADMIN) {
		variants.add(JSW.userRole.siteAdmin);
		variants.add(JSW.userRole.admin);
	}

	if (projectState?.edition === FREE_EDITION) {
		variants.add(JSW.edition.free);
	} else if (projectState?.edition === STANDARD_EDITION) {
		variants.add(JSW.edition.standard);
	} else if (projectState?.edition === PREMIUM_EDITION) {
		variants.add(JSW.edition.premium);
	}

	if (isRoadmapRoute(routeName)) {
		variants.add(JSW.route.softwareRoadmap);
	} else if (routeName === 'software-backlog') {
		variants.add(JSW.route.softwareBacklog);
	} else if (routeName === 'software-boards') {
		variants.add(JSW.route.softwareBoard);
	} else if (routeName === 'software-list' || routeName === 'software-list-classic') {
		variants.add(JSW.route.softwareList);
	} else if (routeName === 'project-settings-software-access') {
		variants.add(JSW.route.projectSettingsSoftwareAccess);
	} else if (routeName === 'issue') {
		variants.add(JSW.route.issue);
	} else {
		variants.add(JSW.route.softwareCalendar);
	}

	return getTaskListItem({
		variants: Array.from(variants.values()),
		taskVariants: JSW.taskVariants,
		taskList: JSW.taskList,
		taskGroupParent,
	});
};

const createJwmTasks = ({
	projectState,
	taskGroupParent,
	taskList,
	teamType,
	isEligibleForTailoredOnboardingM2P5,
}: {
	projectState: ProjectState | null;
	taskGroupParent: string;
	taskList?: TaskList;
	teamType?: string;
	isEligibleForTailoredOnboardingM2P5?: boolean;
}): TaskListItem => {
	const variants: string[] = [];

	variants.push(JWM.experiments.projectTheming);

	// No longer reference isEligibleForTailoredOnboardingM2P5 before showing TO cards
	// isEligibleForTailoredOnboardingM2P5 has checks for trello/site admin and team type but these are now built into the config (admin check is removed)
	if (fg('to-cleanup-release-business-cards-to-nth-users')) {
		return createTailoredOnboardingBusinessTasks({
			taskGroupParent,
			teamType,
			projectState,
			taskList,
		});
	}

	// TODO: Remove below logic when cleaning up to-cleanup-release-business-cards-to-nth-users
	// Old JWM config cards can also be removed
	// We have confirmation to remove old cards https://hello.atlassian.net/wiki/spaces/~620663553/pages/5628213263/Quickstart+cards+to+check
	if (isEligibleForTailoredOnboardingM2P5) {
		return createTailoredOnboardingTasksM2P5({
			taskGroupParent,
			teamType,
		});
	}

	if (projectState?.isProjectAdmin) {
		variants.push(JWM.userRole.projectAdmin);
	}

	if (projectState?.userRole === USER_ROLE.JIRA_ADMIN) {
		variants.push(JWM.userRole.jiraAdmin);
	}

	if (projectState?.userRole === USER_ROLE.SITE_ADMIN) {
		variants.push(JWM.userRole.siteAdmin);
	}

	if (projectState?.isSampleProject) {
		variants.push(JWM.experiments.isSampleProject);
	} else {
		variants.push(JWM.experiments.isNotSampleProject);
	}

	if (projectState?.isTrelloCrossflow) {
		variants.push(JWM.experiments.trelloCrossflow);
	}

	return getTaskListItem({
		variants,
		taskVariants: JWM.taskVariants,
		taskList: taskList ?? JWM.taskList,
		taskGroupParent,
	});
};

const createJsmTasks = (
	projectState: ProjectState | null,
	taskGroupParent: string,
): TaskListItem => {
	const variants: string[] = [];

	variants.push(JSM.experiments.projectTheming);

	if (projectState?.isProjectAdmin) {
		variants.push(JSM.userRole.projectAdmin);
	}

	if (projectState?.userRole === USER_ROLE.JIRA_ADMIN) {
		variants.push(JSM.userRole.jiraAdmin);
	}

	if (projectState?.userRole === USER_ROLE.SITE_ADMIN) {
		variants.push(JSM.userRole.siteAdmin);
	}

	return getTaskListItem({
		variants,
		taskVariants: JSM.taskVariants,
		taskList: JSM.taskList,
		taskGroupParent,
	});
};

type TailoredOnboardingVariants =
	| (typeof Experiment)[keyof typeof Experiment]
	| (typeof FeatureGate)[keyof typeof FeatureGate]
	| (typeof TeamType)[keyof typeof TeamType]
	| (typeof Feature)[keyof typeof Feature]
	| (typeof JWM.experiments)[keyof typeof JWM.experiments]
	| (typeof JWM.userRole)[keyof typeof JWM.userRole];

const createPlanTasks = (
	taskGroupParent: string,
	features?: { hasPlanDependencies?: boolean },
): TaskListItem => {
	const variants: string[] = [];

	if (features?.hasPlanDependencies) {
		variants.push(Feature.hasPlanDependencies);
	}

	return getTaskListItem({
		variants,
		taskVariants: PLAN.taskVariants,
		taskList: PLAN.taskList,
		taskGroupParent,
	});
};

export const createTailoredOnboardingTasks = (
	taskGroupParent: string,
	teamType?: string,
	features?: {
		hasBacklog?: boolean;
		hasSprint?: boolean;
		hasSummaryPage?: boolean;
		hasRoadmap?: boolean;
		hasList?: boolean;
		hasCalendar?: boolean;
		hasCode?: boolean;
		hasForms?: boolean;
		hasPages?: boolean;
	},
	isEligibleForTailoredOnboardingScrumAndSoftwareKanban?: boolean,
): TaskListItem => {
	const variants: Array<TailoredOnboardingVariants> = [];

	if (features?.hasBacklog) {
		variants.push(Feature.hasBacklog);
	}

	if (features?.hasSprint) {
		variants.push(Feature.hasSprint);
	}

	if (features?.hasSummaryPage && fg('to-cleanup-remove-initial-project-template-name')) {
		variants.push(Feature.hasSummaryPage);
	}

	if (features?.hasRoadmap && fg('to-cleanup-remove-initial-project-template-name')) {
		variants.push(Feature.hasRoadmap);
	}

	if (features?.hasList && fg('to-cleanup-remove-initial-project-template-name')) {
		variants.push(Feature.hasList);
	}

	if (features?.hasCalendar && fg('to-cleanup-remove-initial-project-template-name')) {
		variants.push(Feature.hasCalendar);
	}

	if (features?.hasCode && fg('to-cleanup-remove-initial-project-template-name')) {
		variants.push(Feature.hasCode);
	}

	if (features?.hasForms && fg('to-cleanup-remove-initial-project-template-name')) {
		variants.push(Feature.hasForms);
	}

	if (features?.hasPages && fg('to-cleanup-remove-initial-project-template-name')) {
		variants.push(Feature.hasPages);
	}

	if (
		!fg('to-cleanup-remove-initial-project-template-name') &&
		isEligibleForTailoredOnboardingScrumAndSoftwareKanban
	) {
		variants.push(Experiment.tailoredOnboardingScrumAndSoftwareKanbanEligible);
	}

	if (fg('to-cleanup-remove-initial-project-template-name')) {
		variants.push(FeatureGate.removeInitialProjectTemplateName);
	}

	if (teamType && isTailoredOnboardingTeamType(teamType)) {
		variants.push(`teamType.${teamType}`);
	} else {
		variants.push('teamType.everythingElse');
	}

	return getTaskListItem({
		variants,
		taskVariants: fg('to-cleanup-remove-initial-project-template-name')
			? TAILORED_ONBOARDING_WITH_SINGLE_CONFIG.taskVariants
			: TAILORED_ONBOARDING_WITH_SINGLE_CONFIG.taskVariantsOld,
		taskList: TAILORED_ONBOARDING_WITH_SINGLE_CONFIG.taskList,
		taskGroupParent,
	});
};

export const createTailoredOnboardingTasksM2P5 = ({
	taskGroupParent,
	teamType,
}: {
	taskGroupParent: string;
	teamType: string | undefined;
}): TaskListItem => {
	const variants: TailoredOnboardingVariants[] = [];

	if (teamType && isTailoredOnboardingM2P5TailoredTeamType(teamType)) {
		variants.push(`teamType.${teamType}`);
	} else {
		variants.push('teamType.everythingElse');
	}

	return getTaskListItem({
		variants,
		taskVariants: TAILORED_ONBOARDING_M2P5.getM2P5TaskVariants(teamType ?? 'everythingElse'),
		taskList: TAILORED_ONBOARDING_M2P5.taskList,
		taskGroupParent,
	});
};

export const createTailoredOnboardingBusinessTasks = ({
	taskGroupParent,
	teamType,
	projectState,
	taskList,
}: {
	taskGroupParent: string;
	teamType: string | undefined;
	projectState?: ProjectState | null;
	taskList?: TaskList;
}): TaskListItem => {
	const variants: TailoredOnboardingVariants[] = [];

	// When cleaning up M2 and consolidating configs, this team type check should just be updated to include MKT & OPS
	if (teamType && isTailoredOnboardingM2P5TailoredTeamType(teamType)) {
		variants.push(`teamType.${teamType}`);
	} else {
		variants.push('teamType.everythingElse');
	}

	if (projectState?.isTrelloCrossflow) {
		variants.push(JWM.experiments.trelloCrossflow);
	}

	if (projectState?.userRole === USER_ROLE.SITE_ADMIN) {
		variants.push(JWM.userRole.siteAdmin);
	}

	return getTaskListItem({
		variants,
		taskVariants: TAILORED_ONBOARDING_BUSINESS_CONFIG,
		// This should be cleaned up when M2 is cleaned up. Currently it's like this because M2 populates taskList via the BE
		// and then the FE uses it here, ideally M2 cards are in the business config following conventions so they don't get extra priority bypassing other stuff in the config
		// For now there is also an extra check here to show the trello task list for Trello Crossflow users over M2 https://atlassian.slack.com/archives/C082QR7B16Z/p1755066367960509?thread_ts=1755064885.015779&cid=C082QR7B16Z
		taskList:
			!projectState?.isTrelloCrossflow && taskList
				? taskList
				: TAILORED_ONBOARDING_BUSINESS.taskList,
		taskGroupParent,
	});
};

export const getAllTasks = ({
	projectState,
	taskGroupParent,
	routeName,
	shouldShowScrumOnboardingExperience,
	mountedNudges,
	canCreateProject,
	isCrossProjectBoardToPlanUpsell,
	// can edit any plan
	canEditPlans,
	premiumTrialOnSignup,
	taskList,
	teamType,
	isEligibleForTailoredOnboardingM2P5,
	isEligibleForTailoredOnboardingM3,
	isEligibleForTailoredOnboardingScrumAndSoftwareKanban,
	features,
	containerType,
}: GetTasksProps): TaskListItem => {
	if (containerType === 'plan' && fg('quickstart_onboarding_in_jira_plans')) {
		return createPlanTasks(taskGroupParent, features);
	}

	if (isJwmProject(projectState)) {
		return createJwmTasks({
			projectState,
			taskGroupParent,
			taskList,
			teamType,
			isEligibleForTailoredOnboardingM2P5,
		});
	}
	if (isJsmProject(projectState)) {
		return createJsmTasks(projectState, taskGroupParent);
	}

	// We cannot do these in the config itself as we need to fallback to createJswTasks for other users
	// And unlike previous QS experiments, we want to change the entire QS for these users so a new config makes sense over variants to the existing JSW one
	if (!fg('to-cleanup-remove-initial-project-template-name')) {
		if (
			isEligibleForTailoredOnboardingM3 ||
			isEligibleForTailoredOnboardingScrumAndSoftwareKanban
		) {
			return createTailoredOnboardingTasks(
				taskGroupParent,
				teamType,
				features,
				isEligibleForTailoredOnboardingScrumAndSoftwareKanban,
			);
		}
	}

	if (fg('to-cleanup-remove-initial-project-template-name')) {
		const tailoredOnboardingTasks = createTailoredOnboardingTasks(
			taskGroupParent,
			teamType,
			features,
		);

		// TODO: Remove length check on cleanup of to-cleanup-remove-initial-project-template-name
		// It is just a safety fallback if tailored onboarding tasks are empty but this should never happen
		if (tailoredOnboardingTasks.items.length > 0) {
			return tailoredOnboardingTasks;
		}

		// TODO: Remove this error on cleanup of to-cleanup-remove-initial-project-template-name
		// This should never happen
		fireErrorAnalytics({
			error: new Error('tailored onboarding tasks are empty'),
			meta: {
				id: 'quickstartApiError',
				packageName: PACKAGE_NAME,
				teamName: PACKAGE_TEAM_NAME,
			},
			attributes: {
				error: 'tailored onboarding tasks are empty',
				teamType,
				hasBacklog: features?.hasBacklog,
				hasSprint: features?.hasSprint,
				hasSummaryPage: features?.hasSummaryPage,
				hasRoadmap: features?.hasRoadmap,
				hasList: features?.hasList,
				hasCalendar: features?.hasCalendar,
				hasCode: features?.hasCode,
				hasForms: features?.hasForms,
				hasPages: features?.hasPages,
			},
			sendToPrivacyUnsafeSplunk: true,
		});
	}

	return createJswTasks(
		projectState,
		taskGroupParent,
		routeName,
		shouldShowScrumOnboardingExperience,
		mountedNudges,
		canCreateProject,
		isCrossProjectBoardToPlanUpsell,
		canEditPlans,
		premiumTrialOnSignup,
	);
};
